import { siteUrlFor } from '../seo/siteUrl';
/**
 * Locale + URL-prefix helpers.
 *
 * URL scheme:
 *   /                → English (no prefix; x-default)
 *   /he/...          → Hebrew
 *   /ar/...          → Arabic
 *
 * English at root keeps the shortest URLs for the dominant SEO market while still
 * giving distinct, indexable URLs for he/ar — which is what hreflang needs.
 */

export const SUPPORTED_LANGS = ['en', 'he', 'ar'];
export const DEFAULT_LANG = 'en';
export const RTL_LANGS = ['ar', 'he'];
export const SITE_ORIGIN = 'https://stylon.co';

const PREFIX_RE = /^\/(he|ar)(?=\/|$)/;

/** Extract language from a pathname (defaults to 'en' if no prefix). */
export function detectLangFromPath(pathname) {
  const m = (pathname || '/').match(PREFIX_RE);
  return m ? m[1] : DEFAULT_LANG;
}

/** Strip the language prefix from a pathname, returning the "logical" path. */
export function stripLangPrefix(pathname) {
  return (pathname || '/').replace(PREFIX_RE, '') || '/';
}

/** BrowserRouter basename for a given language ('' for English root). */
export function basenameFor(lang) {
  return lang === DEFAULT_LANG ? '' : `/${lang}`;
}

/**
 * Build an absolute, language-aware URL for a "logical" path (no prefix).
 *   absoluteUrl('/pricing', 'he') → 'https://stylon.co/he/pricing/'
 *   absoluteUrl('/', 'en')        → 'https://stylon.co/'
 *   absoluteUrl('/', 'he')        → 'https://stylon.co/he/'
 *
 * EVERY path ends with a trailing slash, because that is the URL the hosting
 * serves (build/pricing/index.html → /pricing/; /pricing answers 302). The
 * shape lives in seo/siteUrl.js, shared with the sitemap generator.
 */
export function absoluteUrl(logicalPath, lang) {
  return siteUrlFor(logicalPath, lang, SITE_ORIGIN);
}

/** True if the language is right-to-left. */
export function isRtl(lang) {
  return RTL_LANGS.includes(lang);
}

// ── Root locale auto-redirect ────────────────────────────────────────────────
// The site opens in English at the bare root because the URL prefix is the
// source of truth (see src/index.js). These helpers decide when to "upgrade" a
// human visitor from the English root to their preferred localized URL. They are
// the canonical, unit-tested spec; the pre-React inline script in
// public/index.html mirrors this exact logic (keep the two in sync).

/**
 * Given an explicit saved choice and the browser's language list, decide which
 * localized URL to send an unprefixed (English-root) visitor to.
 *
 * Returns 'he' | 'ar' to redirect there, or null to stay on English.
 *   - An explicit saved choice (LanguageSwitcher → localStorage) always wins,
 *     including 'en' (→ null: "stay English, do not auto-redirect").
 *   - With no saved choice, the first browser language whose base is he/ar wins;
 *     an English preference ranked ahead of he/ar keeps the visitor on English.
 *   - Never returns 'en'/other — we only ever move English → he/ar, which (paired
 *     with only redirecting unprefixed URLs) makes redirect loops impossible.
 */
export function preferredRedirectLang(savedLang, navigatorLanguages) {
  if (savedLang === 'he' || savedLang === 'ar') return savedLang;
  if (savedLang === 'en') return null;
  const langs = Array.isArray(navigatorLanguages) ? navigatorLanguages : [];
  for (const raw of langs) {
    const base = String(raw || '').toLowerCase().split('-')[0];
    if (base === 'he' || base === 'ar') return base;
    if (base === 'en') return null; // English ranked first → stay on English
  }
  return null;
}

// Crawler / prerender user-agents that must NOT be language-redirected, so
// Google indexes the exact URL it fetches (auto-redirecting Googlebot by
// language would make it treat '/' as a redirect to '/he' and break the English
// x-default) and react-snap captures each URL as-is.
const CRAWLER_UA_RE = /bot|crawl|spider|slurp|bing|yandex|baidu|duckduck|facebookexternalhit|embedly|quora|pinterest|whatsapp|telegram|slackbot|discordbot|prerender|headless|reactsnap|lighthouse|gptbot|claudebot|ccbot|perplexity|applebot/i;

/** True for a crawler / prerender UA that must be left on the requested URL. */
export function isCrawlerUA(ua) {
  return CRAWLER_UA_RE.test(String(ua || ''));
}

/**
 * Full root-redirect decision. Returns the destination PATH (prefix + logical
 * path, no query/hash — the caller appends those) to redirect an unprefixed
 * English-root visit to, or null to render in place. Skips crawlers and
 * already-localized URLs, so it can neither loop nor fight the prerender.
 */
export function rootRedirectPath(pathname, { savedLang, navigatorLanguages, userAgent, cachedCountry } = {}) {
  const path = pathname || '/';
  if (detectLangFromPath(path) !== DEFAULT_LANG) return null; // already localized
  if (isCrawlerUA(userAgent)) return null;
  const target = preferredRedirectLang(savedLang, navigatorLanguages);
  if (!target) return null;
  // Hebrew is Israel-only on the marketing site (App.js bounces a non-IL visitor
  // off /he back to /). Never send a KNOWN non-IL visitor to /he, or the two
  // redirects ping-pong forever. An unknown country (null) passes through —
  // App.js only bounces once the IP is actually known, so at worst there is a
  // single corrective hop, never a loop.
  if (target === 'he' && cachedCountry && String(cachedCountry).toUpperCase() !== 'IL') {
    return null;
  }
  const logical = stripLangPrefix(path) || '/';
  return `${basenameFor(target)}${logical === '/' ? '/' : logical}`;
}
